import React, { useEffect, useState } from 'react';
import { Card, Form, Button, Image, Alert, Spinner } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import authemail from "assets/images/auth/email.png";
import * as Yup from "yup";
import { useFormik } from "formik";
import { toast } from 'react-toastify';
import "react-toastify/dist/ReactToastify.css";
import { sendForgotPasswordOTP, verifyOTPAndResetPassword } from 'slices/auth/login/thunk';
import { updateCooldown, resetForgotPassword } from 'slices/auth/login/reducer';
import { createSelector } from 'reselect';

const ParticlesAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <>{children}</>;
};

const ForgotPassword = () => {
  const dispatch: any = useDispatch();
  const navigate = useNavigate();
  const [passwordShow, setPasswordShow] = useState<boolean>(false);

  // Selector for forgot password state
  const selectForgotPassword = createSelector(
    (state: any) => state.Login,
    (login) => ({
      loading: login.forgotPassword?.loading || false,
      error: login.forgotPassword?.error || null,
      otpSent: login.forgotPassword?.otpSent || false,
      cooldown: login.forgotPassword?.cooldown || 0,
      timeActive: login.forgotPassword?.timeActive || false,
    })
  );

  const { loading, error, otpSent, cooldown, timeActive } = useSelector(selectForgotPassword);

  // Cooldown timer effect
  useEffect(() => {
    if (!timeActive || cooldown <= 0) return;

    const timer = setInterval(() => {
      const newCooldown = cooldown - 1;
      dispatch(updateCooldown(newCooldown));
    }, 1000);

    return () => clearInterval(timer);
  }, [timeActive, cooldown, dispatch]);

  // Reset form when component unmounts
  useEffect(() => {
    return () => {
      dispatch(resetForgotPassword());
    };
  }, [dispatch]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: '',
      otp: '',
      password: '',
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email("Invalid email format")
        .required("Please Enter Your Email"),
      password: otpSent
        ? Yup.string()
            .required("Please Enter Your New Password")
            .min(8, "Password must be at least 8 characters")
        : Yup.string(),
      otp: otpSent
        ? Yup.string()
            .required("Please Enter OTP")
            .matches(/^\d{6}$/, "OTP must be exactly 6 digits")
        : Yup.string(),
    }),
    onSubmit: async (values) => {
      try {
        if (!otpSent) {
          // Check cooldown before sending OTP
          if (timeActive && cooldown > 0) {
            toast.warning(
              `Please wait ${Math.floor(cooldown / 60)}m ${cooldown % 60}s before requesting a new OTP.`,
              { toastId: "cooldown-warning" }
            );
            return;
          }

          // Send OTP
          const result = await dispatch(sendForgotPasswordOTP(values.email)).unwrap();
          if (result?.success) {
            toast.success(result.message || "OTP sent to your email successfully", {
              toastId: "otp-sent-success",
            });
          }
        } else {
          // Verify OTP and reset password
          const result = await dispatch(
            verifyOTPAndResetPassword({
              email: values.email,
              otp: values.otp,
              newPassword: values.password,
            })
          ).unwrap();

          if (result?.success) {
            toast.success(result.message || "Password reset successfully", {
              toastId: "resetSuccess",
            });
            validation.resetForm();
            setTimeout(() => navigate("/login"), 1000);
          }
        }
      } catch (error: any) {
        // Error is already handled in the thunk and displayed via Redux state
        // Additional error handling can be done here if needed
        console.error("Forgot password error:", error);
      }
    },
  });

  return (
    <ParticlesAuth>
      <div className="col-xxl-6 mx-auto">
        <Card className="card mb-0 border-0 shadow-none mb-0">
          <Card.Body className="p-sm-5 m-lg-4">
            {/* Header Section */}
            <div className="text-center mt-2">
              <h5 className="fs-3xl">Forgot Password?</h5>
              <p className="text-muted mb-4">Reset password with Rnets & Knots</p>
              <div className="pb-4">
                <Image src={authemail} alt="Email icon" className="avatar-md" />
              </div>
            </div>

            {/* Info Alert */}
            <Alert className="border-0 alert-warning text-center mb-2 mx-2" role="alert">
              {otpSent
                ? "Please enter the OTP sent to your email and your new password."
                : "Enter your email and OTP will be sent to you!"}
            </Alert>

            {/* Error Display */}
            {error && (
              <Alert variant="danger" className="mb-3 mx-2" role="alert">
                {error}
              </Alert>
            )}

            {/* Form Section */}
            <div className="p-2">
              <Form
                onSubmit={(e) => {
                  e.preventDefault();
                  validation.handleSubmit();
                }}
                noValidate
              >
                {/* Email Field */}
                <div className="mb-4">
                  <Form.Label className="form-label">
                    Email <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="email"
                    className="form-control"
                    id="email"
                    placeholder="Enter Email"
                    required
                    name="email"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.email || ""}
                    isInvalid={validation.touched.email && !!validation.errors.email}
                    disabled={otpSent}
                    autoComplete="email"
                  />
                  <Form.Control.Feedback type="invalid">
                    {validation.errors.email}
                  </Form.Control.Feedback>
                </div>

                {/* OTP and Password Fields - Only shown after OTP is sent */}
                {otpSent && (
                  <>
                    {/* Password Field */}
                    <div className="mb-3">
                      <Form.Label className="form-label">
                        New Password <span className="text-danger">*</span>
                      </Form.Label>
                      <div className="position-relative auth-pass-inputgroup">
                        <Form.Control
                          type={passwordShow ? "text" : "password"}
                          placeholder="Enter new password"
                          required
                          name="password"
                          value={validation.values.password || ""}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          isInvalid={
                            validation.touched.password && !!validation.errors.password
                          }
                          autoComplete="new-password"
                        />
                        <Form.Control.Feedback type="invalid">
                          {validation.errors.password}
                        </Form.Control.Feedback>
                        <Button
                          variant="link"
                          className="position-absolute end-0 top-0 mt-1 me-2 text-decoration-none text-muted"
                          type="button"
                          onClick={() => setPasswordShow(!passwordShow)}
                          style={{ zIndex: 2 }}
                          aria-label={passwordShow ? "Hide password" : "Show password"}
                        >
                          <i
                            className={passwordShow ? "ri-eye-fill" : "ri-eye-off-fill"}
                          ></i>
                        </Button>
                      </div>
                    </div>

                    {/* OTP Field */}
                    <div className="mb-4">
                      <Form.Label className="form-label">
                        OTP <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        placeholder="Enter 6-digit OTP"
                        required
                        name="otp"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.otp || ""}
                        isInvalid={validation.touched.otp && !!validation.errors.otp}
                        autoComplete="one-time-code"
                      />
                      <Form.Control.Feedback type="invalid" className="d-block">
                        {validation.errors.otp}
                      </Form.Control.Feedback>
                      {timeActive && cooldown > 0 && (
                        <Form.Text className="text-muted d-block mt-1">
                          Resend OTP available in {Math.floor(cooldown / 60)}m {cooldown % 60}s
                        </Form.Text>
                      )}
                    </div>
                  </>
                )}

                {/* Submit Button */}
                <div className="text-center mt-4">
                  <Button
                    className="btn btn-primary w-100"
                    type="submit"
                    disabled={loading || (timeActive && cooldown > 0 && !otpSent)}
                    style={{ minHeight: "42px" }}
                  >
                    {loading ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        {otpSent ? "Resetting Password..." : "Sending OTP..."}
                      </>
                    ) : (
                      otpSent ? "Reset Password" : "Send OTP"
                    )}
                  </Button>
                </div>

                {/* Back to Login Link */}
                {!otpSent && (
                  <div className="mt-4 text-center">
                    <p className="mb-0">
                      Wait, I remember my password...{" "}
                      <Link
                        to="/login"
                        className="fw-semibold text-primary text-decoration-underline"
                      >
                        Click here
                      </Link>
                    </p>
                  </div>
                )}
              </Form>
            </div>
          </Card.Body>
        </Card>
      </div>
    </ParticlesAuth>
  );
};

export default ForgotPassword;
